/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import store from '@/store';
import request from '@hb/sharp-request';
import requestDefault from '@/shared/utils/request';

import { landData } from '@/shared/utils/landData';
import { PreQueryChannleProduct } from '@/model/preApplyResult';
import { newGatewayUrl } from '@/constant';
import platform from '@/shared/utils/platform';
import type { ApplyChooseList } from '@/views_v2/pre-apply-choose/types';
import type { ApplyOrderStatus } from '@/views_v2/pre-apply-result/types';
import { CommodityPo } from '@/po/commodity';
import type {
  CreditApplyTaskType, dcCreditSceneEnum, DcCreditApplyParams, UserStayStatusSceneEnum,
  PrefilterDispatchTypeEnum,
  CreditTaskResultType,
  MiddleIncomeLinkInfoResult,
} from './types';

// 多渠道list
export const getMultiApplyChooseList = (
  /** 设备指纹 */
  riskControlData?: {
    'fingerprint-hash': string;
  },
  /** 流量初筛分发类型 */
  dispatchType?: PrefilterDispatchTypeEnum,
  /** 透传参数 */
  extraData?: Record<string, unknown>,
): Promise<ApplyChooseList> => requestDefault.postGate(
  'f.loan.prefilter.querytask',
  {
    riskControlData,
    source: landData.getSouce(),
    cityInfoJson: store.state.base.cityInfo,
    deviceJson: store.getters['base/devicesJSON'],
    /**
     * 参数新增appendFollowLoan,处理缓存流量
     * 对应后端方案（方案里有需求文档链接）https://hellobike.yuque.com/yxr77w/hmqvkh/aw40q8xi9rl314lk#XDw6Q
     */
    appendFollowLoan: true,
    dispatchType,
    extraData,
  },
  {
    timeout: 30000,
    showLoading: undefined,
    abortXhr: true,
    showErrMsg: undefined,
  },
);

// 检查活体状态是否有效
export const checkocr = (): Promise<null> => request.postGate(
  'f.loan.prefilter.checkocr',
  {},
  {
    baseURL: newGatewayUrl,
    showLoading: undefined,
  },
);

/**
 * 静默检查活体
 * 有活体记录，则返回true，否则返回false
 */
export const silentCheckocr = async (): Promise<boolean> => {
  try {
    await requestDefault.postGate(
      'f.loan.prefilter.checkocr',
      {},
      {
        baseURL: newGatewayUrl,
        showLoading: undefined,
        showErrMsg: undefined,
      },
    );

    return true;
  } catch (error) {
    return false;
  }
};

// 多渠道下单
export const applyMulty = (params:CreditApplyTaskType): Promise<{ taskId: string }> => request.postGate('f.credit.apply.task', {
  ...params,
  deviceJson: store.getters['base/devicesJSON'],
  cityInfoJson: store.state.base.cityInfo,
  source: landData.getSouce(),
  ...platform.currentPlatformType,
},
{
  baseURL: newGatewayUrl,
});

// 半流程多渠道下单
export const dcCreditApply = (
  params: DcCreditApplyParams,
): Promise<{ taskId: string }> => requestDefault
  .postGate(
    'f.dc.credit.apply',
    {
      deviceJson: store.getters['base/devicesJSON'],
      cityInfoJson: store.state.base.cityInfo,
      source: landData.getSouce(),
      ...params,
    },
  );

// 查询用户多渠道额度授信审批状态
export const getMultyPreApplyResult = (
  taskId: string,
): Promise<PreQueryChannleProduct.Infomation> => request.postGate('f.credit.query.task', {
  taskId,
  ...platform.currentPlatformType,
},
{
  baseURL: newGatewayUrl,
});

// 查询外采授信额度申请状态
export const getSourcePreApplyResult = (
  orderId: string,
): Promise<{
  status: ApplyOrderStatus
}> => requestDefault.postGate('f.out.order.state.query', {
  orderId,
  scene: 'credit',
  sourceId: landData.getSourceProductId(),
});

/** 贷超半流程授信渠道获取
 * 与f.loan.prefilter.querytask返回结构一致，但只返回commoditys，commoditys中只返回commodityId、commodityName、icon
 */
export const getDcPrefilter = (params: {
  scene: dcCreditSceneEnum,
  // 资料不完整标识
  incompleteInfo?: boolean,
}): Promise<ApplyChooseList> => requestDefault
  .postGate(
    'f.dc.prefilter',
    {
      ...params,
    },
    {
      showLoading: undefined,
      showErrMsg: undefined,
      abortXhr: true,
    },
  );


// 授信失败abtest网关接口 API_
export const getAbtestApi = (exp: string): Promise<number> => request.postGate(
  'f.common.ab.get',
  {
    exp,
  },
  { baseURL: newGatewayUrl },
);

// 是否显示保险
export const checkInsurance = (): Promise<boolean> => request.postGate(
  'f.insurance.check',
  {},
  {
    baseURL: newGatewayUrl,
  },
);

/** 上报用户停留时长是否达标 */
export const reportUserStayStatus = (params: {
  scene: UserStayStatusSceneEnum,
  commodityModelList?: CommodityPo.Info[]
} & Record<string, unknown>): Promise<void> => request.postGate(
  'f.preCredit.apply',
  {
    ...params,
  },
  {
    baseURL: newGatewayUrl,
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/** 获取授信申请结果 */
export const getCreditTaskResult = (
  creditTaskId: string,
):
Promise<CreditTaskResultType> => requestDefault.postGate(
  'f.credit.task.v2.query',
  { creditTaskId },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/** 获取中收变现外跳链接信息 */
export const getMiddleIncomeLinkInfo = (
): Promise<MiddleIncomeLinkInfoResult> => requestDefault.postGate(
  'f.middle.income.link.info',
  {},
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

const PreApplyChooseAPI = {
  getMultiApplyChooseList,
  checkocr,
  silentCheckocr,
  applyMulty,
  dcCreditApply,
  getMultyPreApplyResult,
  getAbtestApi,
  checkInsurance,
  getSourcePreApplyResult,
  getDcPrefilter,
  reportUserStayStatus,
  getCreditTaskResult,
  getMiddleIncomeLinkInfo,
};

export default PreApplyChooseAPI;
